import React from "react";

import { faRobot } from "@fortawesome/pro-regular-svg-icons";

import {
  useAgentSession,
  useSessionToPlaygroundState,
} from "src/api/agents/execution";
import { NodeExecutionMetadata } from "src/api/types";
import { AgentTraceSkeleton } from "src/base-components/agents/AgentTraceSkeleton";
import { ExecutionLogView } from "src/base-components/agents/ExecutionLogView";
import { SessionActions } from "src/base-components/agents/SessionActions";
import type {
  AgentNode,
  NodeHistoricalRunStateV2,
} from "src/constants/NodeDataTypes";
import { EmptyState } from "src/design-system/EmptyState";

type AgentTraceContentProps = {
  sessionId: string | null;
  isMockData: boolean;
};

const AgentTraceContent: React.FC<AgentTraceContentProps> = ({
  sessionId,
  isMockData,
}) => {
  const sessionQuery = useAgentSession(isMockData ? null : sessionId, {
    polling: true,
  });
  const sessionState = useSessionToPlaygroundState(
    sessionQuery.data,
    isMockData ? null : sessionId,
  );

  if (isMockData) {
    return (
      <EmptyState
        description="Mock data was used for this decision."
        headline="No agent trace available"
        icon={faRobot}
      />
    );
  }

  if (!sessionId) {
    return (
      <EmptyState
        description="Agent trace is not available for this decision."
        headline="No agent trace available"
        icon={faRobot}
      />
    );
  }

  if (sessionQuery.isLoading) {
    return (
      <div className="pt-11">
        <AgentTraceSkeleton />
      </div>
    );
  }

  if (
    sessionQuery.isError ||
    !sessionQuery.data ||
    !sessionState ||
    sessionState.status === "idle"
  ) {
    return (
      <EmptyState
        description="Agent trace could not be loaded."
        headline="No agent trace available"
        icon={faRobot}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex justify-end pt-2 pr-3">
        <SessionActions
          rawSessionData={sessionQuery.data ?? null}
          sessionId={sessionId}
        />
      </div>
      <ExecutionLogView
        key={sessionState.sessionId}
        hideTestInput={false}
        inputTitle="User input"
        sessionState={sessionState}
      />
    </div>
  );
};

type AgentTraceDecisionHistoryTabProps = {
  node: AgentNode;
  runState: NodeHistoricalRunStateV2;
};

export const AgentTraceDecisionHistoryTab: React.FC<
  AgentTraceDecisionHistoryTabProps
> = ({ node, runState }) => {
  const nodeMetadata = runState.nodeExecutionMetadata?.[node.id];
  const sessionId = nodeMetadata?.agent_session_id ?? null;
  const isMockData = nodeMetadata?.data_source === "mock_data";

  return (
    <div className="flex h-full flex-col px-6">
      <AgentTraceContent isMockData={isMockData} sessionId={sessionId} />
    </div>
  );
};

type AgentTraceTestRunTabProps = {
  nodeId: string;
  nodeExecutionMetadata?: NodeExecutionMetadata;
  isFetching: boolean;
};

export const AgentTraceTestRunTab: React.FC<AgentTraceTestRunTabProps> = ({
  nodeId,
  nodeExecutionMetadata,
  isFetching,
}) => {
  const nodeMetadata = nodeExecutionMetadata?.[nodeId];
  const sessionId = nodeMetadata?.agent_session_id ?? null;
  const isMockData = nodeMetadata?.data_source === "mock_data";

  if (isFetching) {
    return (
      <div className="pt-7">
        <AgentTraceSkeleton />
      </div>
    );
  }

  return <AgentTraceContent isMockData={isMockData} sessionId={sessionId} />;
};
